import React, { useEffect, useState, useMemo, useRef } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import {
  Plus, Clock, ExternalLink, Trash2, RefreshCw, ChevronLeft, ChevronRight,
  DollarSign, Upload, CheckCircle2, Sparkles, AlertTriangle, Crown, BarChart3, ListTodo,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import OperationsPulse from "../components/OperationsPulse";
import XDraftActions from "../components/x/XDraftActions";
import { useAuth } from "../context/AuthContext";
import { getToken } from "../lib/api";
import {
  buildCalendarIcs,
  buildRemindersIcs,
  downloadCalendarIcs,
  getCalendarExportEvents,
  getCalendarExportFilename,
  getVisibleCalendarEvents,
  isAdminCalendarSession,
} from "./calendarExport";

// Platform status colors & emojis
const STATUS_CONFIG = {
  // JFF
  jff_presale: { label: "JFF Presale", emoji: "🟡", color: "#F59E0B", bg: "#FFF7E0" },
  jff_sale: { label: "JFF Sale", emoji: "💰", color: "#22c55e", bg: "#E0FFE8" },
  jff_live: { label: "JFF Live", emoji: "✅", color: "#10b981", bg: "#E0FFF4" },
  jff_sub: { label: "JFF Sub", emoji: "📅", color: "#8b5cf6", bg: "#F3E8FF" },
  jff_scheduled: { label: "JFF Scheduled", emoji: "📅", color: "#6b7280", bg: "#f3f4f6" },
  
  // OF VIP
  of_vip_presale: { label: "OF VIP Presale", emoji: "🟡", color: "#00AFF0", bg: "#E0F7FF" },
  of_vip_sale: { label: "OF VIP Sale", emoji: "💰", color: "#22c55e", bg: "#E0FFE8" },
  of_vip_live: { label: "OF VIP Live", emoji: "✅", color: "#10b981", bg: "#E0FFF4" },
  of_vip_sub: { label: "OF VIP Sub", emoji: "📅", color: "#00AFF0", bg: "#E0F7FF" },
  of_vip_scheduled: { label: "OF VIP Scheduled", emoji: "📅", color: "#6b7280", bg: "#f3f4f6" },
  
  // OF FREE
  of_free_preview: { label: "OF FREE Preview", emoji: "👁", color: "#94a3b8", bg: "#f1f5f9" },
  of_free_locked: { label: "OF FREE Locked", emoji: "🔒", color: "#ef4444", bg: "#FFE0E0" },
  
  // RFC
  rfc_uploaded: { label: "RFC Uploaded", emoji: "⬆", color: "#fbbf24", bg: "#FFF7E0" },
  rfc_scheduled: { label: "RFC Scheduled", emoji: "📅", color: "#6b7280", bg: "#f3f4f6" },
  rfc_published: { label: "RFC Published", emoji: "✅", color: "#10b981", bg: "#E0FFF4" },
  
  // Finance
  sale: { label: "Sale", emoji: "💰", color: "#22c55e", bg: "#E0FFE8" },
  tip: { label: "Tip", emoji: "💎", color: "#a855f7", bg: "#F3E8FF" },
  subscription: { label: "Subscription", emoji: "⭐", color: "#00AFF0", bg: "#E0F7FF" },
  payout: { label: "Payout", emoji: "🏦", color: "#f59e0b", bg: "#FFF4E0" },
  
  // X
  tweet: { label: "Tweet Scheduled", emoji: "𝕏", color: "#1DA1F2", bg: "#E7F3FF" },
  tweet_posted: { label: "Tweet Posted", emoji: "✅", color: "#10b981", bg: "#E0FFF4" },
  
  // Social
  social_post: { label: "Social Post", emoji: "📱", color: "#22c55e", bg: "#E0FFE8" },
  
  // Catalog
  video_release: { label: "Video Release", emoji: "🎬", color: "#8b5cf6", bg: "#F3E8FF" },
  shoot: { label: "Shoot", emoji: "📹", color: "#3b82f6", bg: "#E0F2FF" },
  
  // Store
  store_release: { label: "Store Release", emoji: "🛒", color: "#FFD700", bg: "#FFF9E6" },
  
  // Companion Bookings
  booking_pending: { label: "Booking Pending", emoji: "🟡", color: "#F59E0B", bg: "#FFF7E0" },
  booking_approved: { label: "Booking Approved", emoji: "✅", color: "#22c55e", bg: "#E0FFE8" },
  booking_declined: { label: "Booking Declined", emoji: "❌", color: "#ef4444", bg: "#FFE0E0" },
  booking_completed: { label: "Booking Completed", emoji: "✔️", color: "#10b981", bg: "#E0FFF4" },
  booking_cancelled: { label: "Booking Cancelled", emoji: "🚫", color: "#6b7280", bg: "#f3f4f6" },
  
  // Finance
  finance_income: { label: "Income", emoji: "💰", color: "#22c55e", bg: "#E0FFE8" },
  theblowers_payment: { label: "TheBlowers Payment", emoji: "↗", color: "#06b6d4", bg: "#E0F9FC" },
  finance_expense: { label: "Expense", emoji: "💸", color: "#ef4444", bg: "#FFE0E0" },
  finance_import: { label: "Finance Import", emoji: "📊", color: "#3b82f6", bg: "#E0F2FF" },
  
  // Model Applications
  app_pending: { label: "Application Received", emoji: "📝", color: "#F59E0B", bg: "#FFF7E0" },
  app_approved: { label: "Application Approved", emoji: "✅", color: "#22c55e", bg: "#E0FFE8" },
  app_rejected: { label: "Application Rejected", emoji: "❌", color: "#ef4444", bg: "#FFE0E0" },
  decision_approved: { label: "Approved", emoji: "✅", color: "#22c55e", bg: "#E0FFE8" },
  decision_rejected: { label: "Rejected", emoji: "❌", color: "#ef4444", bg: "#FFE0E0" },
  
  // Unified Submissions
  submission_pending: { label: "Proposal Received", emoji: "📋", color: "#F59E0B", bg: "#FFF7E0" },
  submission_approved: { label: "Proposal Accepted", emoji: "✅", color: "#22c55e", bg: "#E0FFE8" },
  submission_rejected: { label: "Proposal Declined", emoji: "❌", color: "#ef4444", bg: "#FFE0E0" },
  collab_approved: { label: "Collab Approved", emoji: "🤝", color: "#22c55e", bg: "#E0FFE8" },
  collab_rejected: { label: "Collab Declined", emoji: "🚫", color: "#ef4444", bg: "#FFE0E0" },
  
  // Specific submission types
  studio_submission: { label: "Studio Proposal", emoji: "🎬", color: "#8b5cf6", bg: "#F3E8FF" },
  event_submission: { label: "Event Proposal", emoji: "🎤", color: "#3b82f6", bg: "#E0F2FF" },
  brand_submission: { label: "Brand Proposal", emoji: "🏢", color: "#f59e0b", bg: "#FFF7E0" },
  model_application: { label: "Model Application", emoji: "👤", color: "#a855f7", bg: "#F3E8FF" },
};

// Helper functions for AI alerts severity styling
const getSeverityColor = (severity) => {
  switch(severity) {
    case "critical": return "#ef4444";
    case "high": return "#f59e0b";
    case "medium": return "#fbbf24";
    default: return "#3b82f6";
  }
};

const getSeverityBg = (severity) => {
  switch(severity) {
    case "critical": return "rgba(239, 68, 68, 0.1)";
    case "high": return "rgba(245, 158, 11, 0.1)";
    case "medium": return "rgba(251, 191, 36, 0.1)";
    default: return "rgba(59, 130, 246, 0.1)";
  }
};

export default function AdvancedCalendar() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isCalendarAdmin = !authLoading && isAdminCalendarSession(user, getToken());
  const [view, setView] = useState("week"); // month | week | day | hours
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedPlannerIds, setSelectedPlannerIds] = useState([]);
  const [platformFilter, setPlatformFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [shiftDays, setShiftDays] = useState(7);
  const plannerInitialized = useRef(false);
  const [loading, setLoading] = useState(true);
  const [calendarLoadError, setCalendarLoadError] = useState("");
  const [calendarExportFeedback, setCalendarExportFeedback] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [aiAlerts, setAiAlerts] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [hubContext, setHubContext] = useState({ vip: null, free: null, social: null, theblowers: null });
  const [newEvent, setNewEvent] = useState({
    title: "",
    content_type: "task",
    scheduled_for: "",
    notes: "",
    priority: "medium"
  });

  useEffect(() => {
    loadAll();
  }, [currentDate, view, isCalendarAdmin, authLoading]);

  useEffect(() => {
    setSelectedPlannerIds([]);
  }, [currentDate, view, platformFilter, statusFilter]);

  const loadAll = async ({ sync = false } = {}) => {
    if (authLoading) return;
    if (!isCalendarAdmin) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setCalendarLoadError("");
    try {
      // Date range based on view
      const startDate = getViewStartDate(currentDate, view);
      const endDate = getViewEndDate(currentDate, view);

      if (!plannerInitialized.current || sync) {
        await api.post("/api/planner/sync");
        plannerInitialized.current = true;
      }
      const [plannerResponse, aiAlertsResponse, vipStats, freeStats, socialSummary, theBlowersStats] = await Promise.all([
        api.get("/api/planner/events", { params: { start: startDate, end: endDate, limit: 2000 } }),
        api.get("/api/ai-manager/alerts").catch(() => ({ data: { alerts: [] } })),
        api.get("/api/onlyfans/stats/vip").catch(() => ({ data: null })),
        api.get("/api/onlyfans/stats/free").catch(() => ({ data: null })),
        api.get("/api/social-posts/summary").catch(() => ({ data: null })),
        api.get("/api/theblowers/stats").catch(() => ({ data: null })),
      ]);
      setAiAlerts(aiAlertsResponse.data.alerts || []);
      setHubContext({ vip: vipStats.data, free: freeStats.data, social: socialSummary.data, theblowers: theBlowersStats.data });
      const plannerEvents = (plannerResponse.data.events || []).map((event) => {
        let status = event.content_type || event.status || "task";
        if (event.content_type === "finance_income") status = event.platform === "theblowers" ? "theblowers_payment" : "finance_income";
        if (event.content_type === "finance_expense") status = "finance_expense";
        if (event.content_type === "social_post") status = "social_post";
        if (event.content_type === "tweet") status = String(event.status).toLowerCase() === "posted" ? "tweet_posted" : "tweet";
        if (event.content_type === "booking") status = `booking_${event.status || "pending"}`;
        if (event.content_type === "presale" && String(event.platform).toLowerCase().includes("justforfans")) status = "jff_presale";
        if (event.content_type === "presale" && String(event.platform).toLowerCase().includes("onlyfans")) status = "of_vip_presale";
        if (event.content_type === "live" && String(event.platform).toLowerCase().includes("justforfans")) status = "jff_live";
        if (event.content_type === "live" && String(event.platform).toLowerCase().includes("onlyfans")) status = "of_vip_live";
        if (event.source_type === "finance_import") status = "finance_import";
        if (event.source_type === "model_application") {
          status = event.content_type === "model_decision"
            ? (event.status === "approved" ? "decision_approved" : "decision_rejected")
            : (event.status === "approved" ? "app_approved" : event.status === "rejected" ? "app_rejected" : "app_pending");
        }
        if (event.source_type === "unified_submission") {
          status = event.content_type.endsWith("_decision")
            ? (event.status === "approved" ? "collab_approved" : "collab_rejected")
            : (event.status === "approved" ? "submission_approved" : event.status === "rejected" ? "submission_rejected" : "submission_pending");
        }
        return {
          id: event.id,
          plannerId: event.id,
          type: event.content_type || "task",
          status,
          title: event.title,
          description: event.notes,
          time: new Date(event.scheduled_for),
          platforms: event.platform ? [event.platform.toLowerCase()] : [],
          price: null,
          source: "planner",
          sourceType: event.source_type,
          sourceId: event.source_id,
          sourceUrl: event.source_url,
          rfcPeriodBreakdown: event.rfc_period_breakdown || null,
          data: event,
        };
      }).filter((event) => !Number.isNaN(event.time.getTime()));
      setEvents(plannerEvents.sort((a, b) => a.time - b.time));
    } catch (error) {
      console.error("Calendar load error:", error);
      setEvents([]);
      setCalendarLoadError("Calendar data could not be loaded.");
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const getViewStartDate = (date, view) => {
    const d = new Date(date);
    if (view === "month") {
      d.setDate(1);
    } else if (view === "week") {
      d.setDate(d.getDate() - d.getDay());
    }
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const getViewEndDate = (date, view) => {
    const d = new Date(date);
    if (view === "month") {
      d.setMonth(d.getMonth() + 1);
      d.setDate(0);
    } else if (view === "week") {
      d.setDate(d.getDate() - d.getDay() + 6);
    } else if (view === "day") {
      d.setDate(d.getDate() + 1);
    }
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  const dateRange = useMemo(() => ({
    start: getViewStartDate(currentDate, view),
    end: getViewEndDate(currentDate, view),
  }), [currentDate, view]);

  const visibleEvents = useMemo(() => getVisibleCalendarEvents(events, {
    ...dateRange,
    platform: platformFilter,
    status: statusFilter,
  }), [events, dateRange, platformFilter, statusFilter]);

  const selectedVisibleIds = useMemo(() => {
    const visibleIds = new Set(visibleEvents.map((event) => String(event.id)));
    return selectedPlannerIds.filter((id) => visibleIds.has(String(id)));
  }, [visibleEvents, selectedPlannerIds]);

  const calendarExportEvents = useMemo(
    () => getCalendarExportEvents(visibleEvents, selectedPlannerIds, "calendar"),
    [visibleEvents, selectedPlannerIds]
  );
  const reminderExportEvents = useMemo(
    () => getCalendarExportEvents(visibleEvents, selectedPlannerIds, "reminders"),
    [visibleEvents, selectedPlannerIds]
  );

  const platformOptions = useMemo(() => [...new Set(events.flatMap((event) => event.platforms || []))].filter(Boolean).sort(), [events]);
  const statusOptions = useMemo(() => [...new Set(events.map((event) => event.status).filter(Boolean))].sort(), [events]);

  const navigateDate = (direction) => {
    const d = new Date(currentDate);
    if (view === "month") {
      d.setMonth(d.getMonth() + direction);
    } else if (view === "week") {
      d.setDate(d.getDate() + (direction * 7));
    } else if (view === "day") {
      d.setDate(d.getDate() + direction);
    }
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Group events by date
  const groupedEvents = useMemo(() => {
    if (view === "month") {
      return groupByMonth(visibleEvents, currentDate);
    } else if (view === "week") {
      return groupByWeek(visibleEvents, currentDate);
    } else if (view === "day") {
      return groupByDay(visibleEvents, currentDate);
    }
    return [];
  }, [visibleEvents, currentDate, view]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const togglePlannerSelection = (event) => {
    setSelectedPlannerIds((selected) => selected.includes(event.id)
      ? selected.filter((id) => id !== event.id)
      : [...selected, event.id]);
  };

  const shiftPlannerDates = async (allEvents) => {
    const days = Number(shiftDays);
    if (!Number.isInteger(days) || days === 0) {
      toast.error("Enter a non-zero whole number of days");
      return;
    }
    if (!allEvents && selectedVisibleIds.length === 0) return;
    if (allEvents && !window.confirm(`Shift every planner event by ${days} days?`)) return;
    try {
      const { data } = await api.post("/api/planner/events/bulk-shift", {
        shift_days: days,
        event_ids: allEvents ? null : selectedVisibleIds,
        all_events: allEvents,
      });
      toast.success(`Shifted ${data.shifted} events by ${days} days`);
      setSelectedPlannerIds([]);
      await loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to shift planner dates");
    }
  };


  // Create new event
  const handleCreateEvent = async () => {
    try {
      await api.post("/api/planner/events", {
        title: newEvent.title,
        content_type: newEvent.content_type,
        scheduled_for: newEvent.scheduled_for,
        notes: newEvent.notes,
        priority: newEvent.priority,
      });
      toast.success("Event created successfully!");
      setShowCreateModal(false);
      setNewEvent({ title: "", content_type: "task", scheduled_for: "", notes: "", priority: "medium" });
      loadAll();
    } catch (error) {
      toast.error("Failed to create event");
      console.error(error);
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/api/planner/events/${eventId}`);
      toast.success("Event deleted");
      setShowEventModal(false);
      loadAll();
    } catch (error) {
      toast.error("Failed to delete event");
      console.error(error);
    }
  };

  const exportCalendarFile = (kind) => {
    if (!isCalendarAdmin) {
      const message = "Admin access is required to export calendar data.";
      setCalendarExportFeedback({ type: "error", message });
      toast.error(message);
      return;
    }
    const exportEvents = kind === "reminders" ? reminderExportEvents : calendarExportEvents;
    if (!exportEvents.length) {
      const message = kind === "reminders" ? "No visible reminders to export." : "No visible calendar events to export.";
      setCalendarExportFeedback({ type: "error", message });
      toast.error(message);
      return;
    }
    try {
      const content = kind === "reminders"
        ? buildRemindersIcs(exportEvents)
        : buildCalendarIcs(exportEvents);
      downloadCalendarIcs(content, getCalendarExportFilename(kind));
      const message = kind === "reminders"
        ? "Apple Reminders download started."
        : "Apple Calendar download started.";
      setCalendarExportFeedback({ type: "success", message });
      toast.success(message);
    } catch {
      const message = "Could not create the calendar download. Please try again.";
      setCalendarExportFeedback({ type: "error", message });
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <AppLayout title="Content Calendar" overline="Advanced Timeline">
        <div className="py-20 text-center" role="status" aria-live="polite" aria-busy="true">
          <RefreshCw className="mx-auto mb-4 animate-spin motion-reduce:animate-none" size={32} style={{ color: "var(--gio-muted)" }} aria-hidden="true" />
          <p style={{ color: "var(--gio-muted)" }}>Loading calendar...</p>
        </div>
      </AppLayout>
    );
  }

  if (!isCalendarAdmin) {
    return (
      <AppLayout title="Content Calendar" overline="Advanced Timeline">
        <p role="alert" className="py-10 text-center" style={{ color: "var(--gio-muted)" }}>
          Admin access is required to view calendar data.
        </p>
      </AppLayout>
    );
  }

  if (calendarLoadError) {
    return (
      <AppLayout title="Content Calendar" overline="Advanced Timeline">
        <div className="py-10 text-center" role="alert">
          <p className="mb-4" style={{ color: "var(--gio-muted)" }}>{calendarLoadError}</p>
          <button type="button" className="btn-ghost min-h-11 px-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]" onClick={() => loadAll()}>
            Retry calendar load
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Content Calendar"
      overline="2022 → Now · JFF · OF VIP · OF FREE · RFC · Finance"
      actions={
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          {/* View Selector */}
          <div className="flex items-center gap-1 rounded-lg border p-1" role="group" aria-label="Calendar view" style={{ borderColor: "var(--gio-border)" }}>
            <ViewButton active={view === "month"} onClick={() => setView("month")}>Month</ViewButton>
            <ViewButton active={view === "week"} onClick={() => setView("week")}>Week</ViewButton>
            <ViewButton active={view === "day"} onClick={() => setView("day")}>Day</ViewButton>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-2" role="group" aria-label="Calendar date navigation">
            <button type="button" onClick={() => navigateDate(-1)} className="btn-ghost min-h-11 min-w-11 p-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]" aria-label="Previous date range">
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <button type="button" onClick={goToToday} className="btn-ghost min-h-11 px-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]">
              Today
            </button>
            <button type="button" onClick={() => navigateDate(1)} className="btn-ghost min-h-11 min-w-11 p-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]" aria-label="Next date range">
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </div>

          <button type="button" onClick={() => loadAll({ sync: true })} className="btn-ghost inline-flex min-h-11 min-w-11 items-center justify-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]" aria-label="Refresh calendar data" title="Refresh calendar data">
            <RefreshCw size={16} aria-hidden="true" />
          </button>

          <div className="inline-flex max-w-full flex-wrap items-center gap-1.5">
            <label className="sr-only" htmlFor="calendar-shift-days">Shift days</label>
            <input
              id="calendar-shift-days"
              aria-label="Days to shift"
              type="number"
              min="-3650"
              max="3650"
              value={shiftDays}
              onChange={(event) => setShiftDays(event.target.value)}
              className="min-h-11 w-16 rounded border px-2 py-2 text-sm"
              style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)", color: "var(--gio-text)" }}
            />
            <button
              type="button"
              onClick={() => shiftPlannerDates(false)}
              disabled={selectedVisibleIds.length === 0}
              className="btn-ghost min-h-11 text-xs disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]"
              title="Shift selected events"
            >
              Shift selected ({selectedVisibleIds.length})
            </button>
            <button
              type="button"
              onClick={() => shiftPlannerDates(true)}
              className="btn-ghost min-h-11 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]"
              title="Shift all planner events"
            >
              Shift all
            </button>
          </div>
          
          <button
            type="button"
            onClick={() => exportCalendarFile("calendar")}
            disabled={!calendarExportEvents.length}
            className="btn-ghost inline-flex min-h-11 items-center gap-2 px-3 text-sm disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]"
            title="Export to Apple Calendar"
            aria-label="Export visible events to Apple Calendar"
          >
            <Upload size={16} aria-hidden="true" /> Export .ics
          </button>

          <button
            type="button"
            onClick={() => exportCalendarFile("reminders")}
            disabled={!reminderExportEvents.length}
            className="btn-ghost inline-flex min-h-11 items-center gap-2 px-3 text-sm disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]"
            title="Add explicit reminder events to Apple Reminders"
            aria-label="Export explicit reminders to Apple Reminders"
          >
            <ListTodo size={16} aria-hidden="true" /> Add to Apple Reminders
          </button>
          
          <button 
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex min-h-11 items-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)]"
          >
            <Plus size={16} /> Create Event
          </button>
        </div>
      }>
      {/* Current Date Display */}
      <div className="mb-5 flex flex-wrap items-end gap-3" data-testid="calendar-filters" aria-label="Calendar filters">
        <div>
          <label htmlFor="calendar-platform-filter" className="mb-1 block text-xs font-semibold" style={{ color: "var(--gio-muted)" }}>Platform</label>
          <select
            id="calendar-platform-filter"
            value={platformFilter}
            onChange={(event) => setPlatformFilter(event.target.value)}
            className="rounded border px-3 py-2 text-sm"
            style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)", color: "var(--gio-text)" }}
          >
            <option value="">All platforms</option>
            {platformOptions.map((platform) => <option key={platform} value={platform}>{platform}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="calendar-status-filter" className="mb-1 block text-xs font-semibold" style={{ color: "var(--gio-muted)" }}>Status</label>
          <select
            id="calendar-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded border px-3 py-2 text-sm"
            style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)", color: "var(--gio-text)" }}
          >
            <option value="">All statuses</option>
            {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </div>
        {(platformFilter || statusFilter) && (
          <button type="button" className="btn-ghost text-sm" onClick={() => { setPlatformFilter(""); setStatusFilter(""); }}>
            Clear filters
          </button>
        )}
      </div>

      {calendarExportFeedback && (
        <p
          role={calendarExportFeedback.type === "error" ? "alert" : "status"}
          aria-live={calendarExportFeedback.type === "error" ? "assertive" : "polite"}
          className="mb-4 text-sm"
          style={{ color: calendarExportFeedback.type === "error" ? "var(--gio-danger)" : "var(--gio-success)" }}
          data-testid="calendar-export-feedback"
        >
          {calendarExportFeedback.message}
        </p>
      )}
      <div className="mb-6 text-center">
        <h2 className="text-3xl font-bold" style={{ color: "var(--gio-text)" }}>
          {currentDate.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
            ...(view === "day" && { day: "numeric" }),
          })}
        </h2>
        {view === "week" && (
          <p className="text-sm mt-1" style={{ color: "var(--gio-muted)" }}>
            Week of {getViewStartDate(currentDate, "week").split("T")[0]}
          </p>
        )}
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="calendar-upstream-context">
        <CalendarContextCard icon={<Crown size={18} />} title="VIP hub" color="#00AFF0" value={hubContext.vip ? `${hubContext.vip.subscribers || 0} subscribers · $${Number(hubContext.vip.total_revenue || 0).toFixed(2)} revenue` : "Metrics unavailable"} detail="Hub-fed context for premium releases and monetization planning." />
        <CalendarContextCard icon={<Crown size={18} />} title="Free hub" color="#22c55e" value={hubContext.free ? `${hubContext.free.followers || 0} followers · ${hubContext.free.post_views || 0} views` : "Metrics unavailable"} detail="Hub-fed audience context for previews and conversion planning." />
        <CalendarContextCard icon={<BarChart3 size={18} />} title="Multi-Tracker" color="#a855f7" value={hubContext.social ? `${hubContext.social.total || 0} tracked posts · ${(hubContext.social.totals?.views || 0).toLocaleString()} views` : "Metrics unavailable"} detail="Posting and engagement context; per-item conversion attribution is unavailable." />
        <CalendarContextCard icon={<DollarSign size={18} />} title={`TheBlowers · ${currentDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`} color="#06b6d4" value={(() => {
          const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
          const rows = (hubContext.theblowers?.paymentsByMonth || []).filter((row) => row.month === monthKey);
          return rows.length ? rows.map((row) => `${Number(row.total || 0).toFixed(2)} ${row.currency || ""}`.trim()).join(" · ") : "No received payments";
        })()} detail="Monthly summary from Invoice History; individual receipts appear on their payment dates." />
      </section>

      {/* AI Business Manager Insights Panel */}
      <OperationsPulse
        title="Planning operations pulse"
        subtitle="Hub releases, tracked posts, revenue events, and actionable schedule context in one view."
        metrics={[
          { label: "Calendar events", value: visibleEvents.length, detail: "current date range and filters" },
          { label: "VIP subscribers", value: hubContext.vip?.subscribers || 0, color: "#00AFF0", detail: "hub context" },
          { label: "Free followers", value: hubContext.free?.followers || 0, color: "#22c55e", detail: "hub context" },
          { label: "AI alerts", value: aiAlerts.length, color: aiAlerts.length ? "#f59e0b" : "#22c55e", detail: "action queue" },
        ]}
        gap="⚠️ Limited: Full scheduling integration, conversion attribution, and provider-level planning contracts coming soon."
      />
      {aiAlerts.length > 0 && showAiPanel && (
        <div className="mb-6 border rounded-lg p-4" style={{ borderColor: "var(--gio-border)", backgroundColor: "rgba(168, 85, 247, 0.05)" }}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-purple-400" />
              <h3 className="font-semibold" style={{ color: "var(--gio-text)" }}>AI Business Insights</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/crm/ai?tab=today")}
                className="text-xs px-3 py-1 rounded-md hover:bg-purple-900/30 transition flex items-center gap-1"
                style={{ color: "var(--gio-accent)" }}
              >
                View Full Dashboard
                <ExternalLink size={12} />
              </button>
              <button
                onClick={() => setShowAiPanel(false)}
                className="text-xs px-2 py-1 rounded hover:bg-purple-900/30 transition"
                style={{ color: "var(--gio-muted)" }}
              >
                ✕
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {aiAlerts.slice(0, 3).map((alert, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-3 rounded-lg"
                style={{ backgroundColor: getSeverityBg(alert.severity) }}
              >
                <span className="text-xl">{alert.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: "var(--gio-text)" }}>{alert.message}</p>
                  {alert.action && (
                    <p className="text-xs mt-1" style={{ color: "var(--gio-muted)" }}>{alert.action}</p>
                  )}
                </div>
                <AlertTriangle size={16} style={{ color: getSeverityColor(alert.severity) }} />
              </div>
            ))}
            {aiAlerts.length > 3 && (
              <p className="text-xs text-center pt-2" style={{ color: "var(--gio-muted)" }}>
                +{aiAlerts.length - 3} more alerts in AI Manager
              </p>
            )}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      {!visibleEvents.length && (
        <p role="status" className="mb-4 text-center text-sm" style={{ color: "var(--gio-muted)" }}>
          No events match the current date range and filters.
        </p>
      )}
      {view === "month" && <MonthView days={groupedEvents} onEventClick={handleEventClick} selectedIds={selectedPlannerIds} onToggleSelect={togglePlannerSelection} />}
      {view === "week" && <WeekView days={groupedEvents} onEventClick={handleEventClick} selectedIds={selectedPlannerIds} onToggleSelect={togglePlannerSelection} />}
      {view === "day" && <DayView events={groupedEvents} onEventClick={handleEventClick} selectedIds={selectedPlannerIds} onToggleSelect={togglePlannerSelection} />}

      {/* Event Modal */}
      {showEventModal && selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onUpdate={loadAll}
          onDelete={handleDeleteEvent}
        />
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full p-6 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-4">Create New Event</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Event Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black/60 border border-gray-700 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  placeholder="Event title"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">Event Type *</label>
                <select
                  value={newEvent.content_type}
                  onChange={(e) => setNewEvent({ ...newEvent, content_type: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black/60 border border-gray-700 text-white focus:border-purple-500 focus:outline-none"
                >
                  <option value="task">Task</option>
                  <option value="shoot">Video Shoot</option>
                  <option value="booking">VIP Booking</option>
                  <option value="collab">Collaboration</option>
                  <option value="meeting">Meeting</option>
                  <option value="deadline">Deadline</option>
                  <option value="release">Content Release</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">Date & Time *</label>
                <input
                  type="datetime-local"
                  value={newEvent.scheduled_for}
                  onChange={(e) => setNewEvent({ ...newEvent, scheduled_for: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black/60 border border-gray-700 text-white focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">Priority</label>
                <select
                  value={newEvent.priority}
                  onChange={(e) => setNewEvent({ ...newEvent, priority: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black/60 border border-gray-700 text-white focus:border-purple-500 focus:outline-none"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium">Notes</label>
                <textarea
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black/60 border border-gray-700 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  rows={4}
                  placeholder="Event description or notes..."
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewEvent({ title: "", content_type: "task", scheduled_for: "", notes: "", priority: "medium" });
                  }}
                  className="px-6 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateEvent}
                  disabled={!newEvent.title || !newEvent.scheduled_for}
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-600 text-white font-medium hover:from-purple-600 hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

// ============================================================================
// VIEW HELPERS
// ============================================================================

function groupByMonth(events, currentDate) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  // Get starting day (Sunday)
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - startDay.getDay());

  // Generate 42 days (6 weeks)
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDay);
    d.setDate(startDay.getDate() + i);
    const key = d.toISOString().split("T")[0];
    days.push({
      date: d,
      isCurrentMonth: d.getMonth() === month,
      items: events.filter(e => e.time.toISOString().split("T")[0] === key),
    });
  }

  return days;
}

function groupByWeek(events, currentDate) {
  const days = [];
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    const key = d.toISOString().split("T")[0];
    days.push({
      date: d,
      isCurrentMonth: true,
      items: events.filter(e => e.time.toISOString().split("T")[0] === key),
    });
  }

  return days;
}

function groupByDay(events, currentDate) {
  return events.filter(e =>
    e.time.toISOString().split("T")[0] === currentDate.toISOString().split("T")[0]
  );
}

// ============================================================================
// VIEW COMPONENTS
// ============================================================================

function MonthView({ days, onEventClick, selectedIds, onToggleSelect }) {
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div>
      {/* Week headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs font-bold uppercase tracking-wider py-2"
            style={{ color: "var(--gio-muted)" }}>
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, i) => (
          <DayCell key={i} day={day} onEventClick={onEventClick} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
        ))}
      </div>
    </div>
  );
}

function WeekView({ days, onEventClick, selectedIds, onToggleSelect }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
      {days.map((day, i) => (
        <DayColumn key={i} day={day} onEventClick={onEventClick} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
      ))}
    </div>
  );
}

function DayView({ events, onEventClick, selectedIds, onToggleSelect }) {
  // Group by hour
  const hours = [];
  for (let h = 0; h < 24; h++) {
    const hourEvents = events.filter(e => e.time.getHours() === h);
    hours.push({ hour: h, items: hourEvents });
  }

  return (
    <div className="space-y-3">
      {hours.map(({ hour, items }) => (
        <div key={hour} className="flex gap-4">
          <div className="w-16 text-right">
            <span className="text-sm font-bold" style={{ color: "var(--gio-muted)" }}>
              {hour.toString().padStart(2, "0")}:00
            </span>
          </div>
          <div className="flex-1 border-l pl-4 space-y-2" style={{ borderColor: "var(--gio-border)" }}>
            {items.length === 0 ? (
              <div className="text-xs py-2" style={{ color: "var(--gio-muted-3)" }}>—</div>
            ) : (
              items.map(item => <EventCard key={item.id} item={item} onClick={() => onEventClick(item)} compact selected={selectedIds?.includes(item.plannerId)} onToggleSelect={onToggleSelect} />)
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function DayCell({ day, onEventClick, selectedIds, onToggleSelect }) {
  const isToday = day.date.toDateString() === new Date().toDateString();

  return (
    <div
      className={`min-h-[120px] p-2 rounded border ${isToday ? "border-[color:var(--gio-primary)] border-2" : ""}`}
      style={{
        background: day.isCurrentMonth ? "var(--gio-surface)" : "var(--gio-surface-2)",
        borderColor: isToday ? "var(--gio-primary)" : "var(--gio-border)",
        opacity: day.isCurrentMonth ? 1 : 0.5,
      }}>
      <div className="text-sm font-bold mb-2" style={{ color: isToday ? "var(--gio-primary)" : "var(--gio-text)" }}>
        {day.date.getDate()}
      </div>
      <div className="space-y-1">
        {day.items.slice(0, 3).map(item => {
          const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.sale;
          return (
            <div key={item.id} className="flex items-center gap-1 min-w-0">
              {item.plannerId && (
                <input
                  type="checkbox"
                  checked={selectedIds?.includes(item.plannerId) || false}
                  aria-label={`Select ${item.title}`}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => onToggleSelect(item)}
                  className="shrink-0"
                />
              )}
              <button
                type="button"
                onClick={() => onEventClick(item)}
                className="text-left text-[10px] px-1.5 py-0.5 rounded truncate min-w-0 flex-1"
                style={{ background: config.bg, color: config.color }}>
                {config.emoji} {item.price && `$${item.price}`} {item.title.slice(0, 20)}
              </button>
            </div>
          );
        })}
        {day.items.length > 3 && (
          <div className="text-[9px] text-center" style={{ color: "var(--gio-muted-3)" }}>
            +{day.items.length - 3} more
          </div>
        )}
      </div>
    </div>
  );
}

function DayColumn({ day, onEventClick, selectedIds, onToggleSelect }) {
  const isToday = day.date.toDateString() === new Date().toDateString();

  return (
    <div className="flex flex-col h-full">
      <div className={`p-3 rounded-t-lg border-b ${isToday ? "border-[color:var(--gio-primary)]" : ""}`}
        style={{
          background: isToday ? "rgba(0,175,240,0.1)" : "var(--gio-surface)",
          borderColor: isToday ? "var(--gio-primary)" : "var(--gio-border)",
        }}>
        <div className="text-xs font-bold uppercase" style={{ color: isToday ? "var(--gio-primary)" : "var(--gio-muted)" }}>
          {day.date.toLocaleDateString("en-US", { weekday: "short" })}
        </div>
        <div className="text-xl font-bold" style={{ color: "var(--gio-text)" }}>{day.date.getDate()}</div>
      </div>
      <div className="flex-1 p-2 space-y-2 min-h-[400px]" style={{ background: "var(--gio-surface-2)" }}>
        {day.items.length === 0 ? (
          <div className="text-center py-8 text-xs" style={{ color: "var(--gio-muted-3)" }}>No events</div>
        ) : (
          day.items.map(item => <EventCard key={item.id} item={item} onClick={() => onEventClick(item)} selected={selectedIds?.includes(item.plannerId)} onToggleSelect={onToggleSelect} />)
        )}
      </div>
    </div>
  );
}

function EventCard({ item, onClick, compact, selected, onToggleSelect }) {
  const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.sale;

  return (
    <div
      onClick={onClick}
      className="p-3 rounded-lg border-2 transition-all hover:scale-[1.02] cursor-pointer"
      title={item.description || item.title}
      style={{
        background: "var(--gio-surface)",
        borderColor: config.color,
        boxShadow: `0 2px 8px -2px ${config.color}40`,
      }}>
      <div className="flex items-center gap-2 mb-2">
        {item.plannerId && (
          <input
            type="checkbox"
            checked={selected || false}
            aria-label={`Select ${item.title}`}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onToggleSelect(item)}
            className="shrink-0"
          />
        )}
        <span className="text-lg">{config.emoji}</span>
        <Clock size={10} style={{ color: "var(--gio-muted-3)" }} />
        <span className="text-xs font-bold" style={{ color: "var(--gio-text)" }}>
          {item.time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>

      <h4 className="text-sm font-bold mb-1 line-clamp-2" style={{ color: "var(--gio-text)" }}>
        {item.title}
      </h4>

      {item.price && (
        <div className="text-xs font-bold mb-2" style={{ color: "#22c55e" }}>
          ${item.price.toFixed(2)}
        </div>
      )}

      {!compact && item.thumbnail && (
        <div className="mb-2 rounded overflow-hidden" style={{ aspectRatio: "16/9" }}>
          <img src={item.thumbnail} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: config.color }}>
        {config.label}
      </div>
      {(item.hubSource || item.engagement || item.revenue) && (
        <div className="mt-2 flex flex-wrap gap-1 text-[9px] font-bold uppercase tracking-wider">
          {item.hubSource && <span className="rounded px-1.5 py-0.5" style={{ color: "var(--gio-muted-2)", background: "var(--gio-surface-hi)" }}>{item.hubSource}</span>}
          {item.engagement > 0 && <span className="rounded px-1.5 py-0.5" style={{ color: "#a855f7", background: "rgba(168,85,247,0.14)" }}>{item.engagement.toLocaleString()} engagement</span>}
          {item.revenue > 0 && <span className="rounded px-1.5 py-0.5" style={{ color: "#22c55e", background: "rgba(34,197,94,0.14)" }}>${item.revenue.toFixed(2)} tracked</span>}
        </div>
      )}
    </div>
  );
}

function CalendarContextCard({ icon, title, color, value, detail }) {
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: `${color}66`, background: "var(--gio-surface)" }}>
      <div className="flex items-center gap-2 text-sm font-semibold" style={{ color }}>{icon}{title}</div>
      <div className="mt-2 text-sm font-medium" style={{ color: "var(--gio-text)" }}>{value}</div>
      <div className="mt-2 text-xs" style={{ color: "var(--gio-muted-2)" }}>{detail}</div>
    </div>
  );
}

function EventModal({ event, onClose, onUpdate, onDelete }) {
  const isNewEvent = !event.id;
  const isCustomEvent = event.source === undefined || event.type === "custom";
  const [isEditing, setIsEditing] = useState(isNewEvent);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: event.title || "",
    scheduled_for: event.time ? new Date(event.time).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
    platform: event.platforms?.[0] || "",
    content_type: event.type || "shoot",
    notes: event.description || "",
  });

  const config = STATUS_CONFIG[event.status] || STATUS_CONFIG.sale;

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        scheduled_for: new Date(formData.scheduled_for).toISOString(),
        platform: formData.platform,
        content_type: formData.content_type,
        notes: formData.notes,
      };

      if (isNewEvent) {
        await api.post("/api/planner/events", payload);
        toast.success("Event created successfully");
      } else {
        await api.patch(`/api/planner/events/${event.plannerId || event.id}`, payload);
        toast.success("Event updated successfully");
      }
      
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(event.plannerId || event.id);
    } finally {
      setDeleting(false);
    }
  };

  const goToSource = () => {
    if (event.sourceUrl) {
      window.open(event.sourceUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const sourceRoutes = {
      x_pipeline: "/crm/x-hub?tab=compose",
      scheduled_tweet: "/crm/x-hub?tab=compose",
      catalog: "/crm/catalog",
      video: "/crm/catalog",
      content: "/crm/catalog",
      multitrack: "/crm/social?tab=tracker",
      social_post: "/crm/social?tab=tracker",
      finance: "/crm/money?tab=imports",
      revenue: "/crm/money?tab=revenue",
      expense: "/crm/money?tab=expenses",
      finance_import: "/crm/money?tab=imports",
      crm_follow_up: "/crm/contacts",
      model_application: "/submissions",
      unified_submission: "/submissions",
    };
    const destination = sourceRoutes[event.sourceType] || sourceRoutes[event.source];
    if (destination) window.location.href = destination;
  };

  // If it's a read-only event (from videos/finance/etc), show comprehensive view
  if (!isCustomEvent && !isEditing) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" />
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl mx-4 rounded-2xl overflow-hidden border-2 shadow-2xl"
          style={{
            background: "linear-gradient(to bottom right, rgba(0,0,0,0.95), rgba(24,24,27,0.95))",
            borderColor: config.color,
            boxShadow: `0 20px 60px -10px ${config.color}66`,
          }}>
          {/* Enhanced Header with Pink/Purple Neon */}
          <div className="p-6 border-b bg-gradient-to-r from-purple-900/20 to-pink-900/20" style={{ borderColor: config.color }}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="text-xs font-bold uppercase mb-2 inline-flex items-center gap-2" style={{ color: config.color }}>
                  {config.emoji} {config.label}
                  {event.source && (
                    <span className="px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-300 text-[10px]">
                      {event.source}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                  {event.title}
                </h2>
                <div className="flex items-center gap-4 text-sm text-zinc-400">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={14} />
                    {event.time.toLocaleString()}
                  </span>
                  {event.platforms && event.platforms.length > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      📱 {event.platforms.join(", ")}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition text-zinc-400 hover:text-white">
                <RefreshCw size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
            {event.rfcPeriodBreakdown && <RFCPeriodBreakdown period={event.rfcPeriodBreakdown} />}
            {event.sourceType === "scheduled_tweet" && event.data?.status === "draft" && (
              <div className="rounded-xl border border-violet-500/30 p-4" data-testid="calendar-x-draft">
                <div className="text-sm font-bold uppercase tracking-wider text-violet-300">X draft · not posted</div>
                <XDraftActions
                  draft={{ id: event.sourceId, text: event.title, scheduled_at: event.data.source_scheduled_for || event.data.scheduled_for }}
                  textEditable={false}
                  onChanged={() => { onUpdate({ sync: true }); onClose(); }}
                />
              </div>
            )}
            {/* Thumbnail */}
            {event.thumbnail && (
              <div className="rounded-xl overflow-hidden border-2 border-purple-500/20">
                <img src={event.thumbnail} alt="" className="w-full" style={{ maxHeight: "300px", objectFit: "cover" }} />
              </div>
            )}

            {/* Financial Information */}
            {(event.price || event.revenue || event.cost) && (
              <div className="bg-gradient-to-br from-green-900/10 to-emerald-900/10 border border-green-500/20 rounded-xl p-5">
                <div className="text-sm font-bold uppercase tracking-wider text-green-400 mb-4 inline-flex items-center gap-2">
                  💰 Financial Details
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {event.price && (
                    <div>
                      <div className="text-xs text-zinc-500 mb-1">Price</div>
                      <div className="text-2xl font-bold text-green-400">${event.price.toFixed(2)}</div>
                    </div>
                  )}
                  {event.revenue && (
                    <div>
                      <div className="text-xs text-zinc-500 mb-1">Revenue</div>
                      <div className="text-2xl font-bold text-emerald-400">${event.revenue.toFixed(2)}</div>
                    </div>
                  )}
                  {event.cost && (
                    <div>
                      <div className="text-xs text-zinc-500 mb-1">Cost</div>
                      <div className="text-2xl font-bold text-red-400">${event.cost.toFixed(2)}</div>
                    </div>
                  )}
                  {event.revenue && event.cost && (
                    <div>
                      <div className="text-xs text-zinc-500 mb-1">Profit</div>
                      <div className="text-2xl font-bold text-cyan-400">${(event.revenue - event.cost).toFixed(2)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Platform Matrix Status */}
            {event.platform_status && (
              <div className="bg-gradient-to-br from-purple-900/10 to-pink-900/10 border border-purple-500/20 rounded-xl p-5">
                <div className="text-sm font-bold uppercase tracking-wider text-purple-400 mb-4 inline-flex items-center gap-2">
                  📊 Platform Matrix
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(event.platform_status).map(([platform, status]) => (
                    <div key={platform} className="bg-black/40 border border-zinc-800 rounded-lg p-3">
                      <div className="text-xs text-zinc-500 mb-1">{platform}</div>
                      <div className="text-sm font-semibold text-white">{status}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CRM Interactions */}
            {event.crm_interactions && event.crm_interactions.length > 0 && (
              <div className="bg-gradient-to-br from-blue-900/10 to-indigo-900/10 border border-blue-500/20 rounded-xl p-5">
                <div className="text-sm font-bold uppercase tracking-wider text-blue-400 mb-4 inline-flex items-center gap-2">
                  👥 CRM Interactions ({event.crm_interactions.length})
                </div>
                <div className="space-y-3">
                  {event.crm_interactions.map((interaction, idx) => (
                    <div key={idx} className="bg-black/40 border border-zinc-800 rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="text-sm font-semibold text-white">{interaction.contact_name}</div>
                        <div className="text-xs text-zinc-500">{new Date(interaction.date).toLocaleDateString()}</div>
                      </div>
                      <div className="text-xs text-zinc-400">{interaction.summary}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description/Notes */}
            {event.description && (
              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                <div className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-3">Description</div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{event.description}</p>
              </div>
            )}

            {/* Additional Metadata */}
            {(event.cast || event.location || event.duration) && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {event.cast && (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                    <div className="text-xs text-zinc-500 mb-1">Cast</div>
                    <div className="text-sm text-white font-medium">{event.cast}</div>
                  </div>
                )}
                {event.location && (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                    <div className="text-xs text-zinc-500 mb-1">Location</div>
                    <div className="text-sm text-white font-medium">{event.location}</div>
                  </div>
                )}
                {event.duration && (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                    <div className="text-xs text-zinc-500 mb-1">Duration</div>
                    <div className="text-sm text-white font-medium">{event.duration}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enhanced Footer */}
          <div className="p-5 border-t bg-gradient-to-r from-purple-900/10 to-pink-900/10 flex justify-between" style={{ borderColor: "rgba(168, 85, 247, 0.2)" }}>
            <button onClick={goToSource} className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 rounded-lg inline-flex items-center gap-2 transition-all">
              <ExternalLink size={16} />
              Open in {event.source === "x_pipeline" ? "X Hub" : event.source === "catalog" ? "Catalog" : "Source"}
            </button>
            <div className="flex gap-2">
              {event.source === "planner" && (
                <>
                  <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50">
                    {deleting ? "Hiding…" : "Hide"}
                  </button>
                  <button onClick={() => setIsEditing(true)} className="px-4 py-2 rounded-lg border border-zinc-600 text-zinc-200 hover:bg-zinc-800">
                    Edit
                  </button>
                </>
              )}
              <button onClick={onClose} className="px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-white font-semibold transition-all">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Editable form for custom calendar events or new events
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)" }} />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl mx-4 rounded-2xl overflow-hidden"
        style={{
          background: "var(--gio-surface)",
          border: "2px solid var(--gio-primary)",
          boxShadow: "0 20px 60px -10px rgba(168, 85, 247, 0.4)",
        }}>
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--gio-border)" }}>
          <div>
            <div className="text-xs font-bold uppercase mb-1" style={{ color: "var(--gio-primary)" }}>
              📅 {isNewEvent ? "NEW EVENT" : "EDIT EVENT"}
            </div>
            <h2 className="text-xl font-bold" style={{ color: "var(--gio-text)" }}>
              {isNewEvent ? "Add Calendar Event" : formData.title}
            </h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <RefreshCw size={24} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="text-xs font-bold uppercase mb-2 block" style={{ color: "var(--gio-muted)" }}>
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border"
              style={{
                background: "var(--gio-surface-alt)",
                borderColor: "var(--gio-border)",
                color: "var(--gio-text)",
              }}
              placeholder="Shoot with [Model Name], Content upload, etc."
            />
          </div>

          {/* Date/Time */}
          <div>
            <label className="text-xs font-bold uppercase mb-2 block" style={{ color: "var(--gio-muted)" }}>
              Scheduled Date & Time *
            </label>
            <input
              type="datetime-local"
              value={formData.scheduled_for}
              onChange={(e) => setFormData({ ...formData, scheduled_for: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border"
              style={{
                background: "var(--gio-surface-alt)",
                borderColor: "var(--gio-border)",
                color: "var(--gio-text)",
              }}
            />
          </div>

          {/* Platform */}
          <div>
            <label className="text-xs font-bold uppercase mb-2 block" style={{ color: "var(--gio-muted)" }}>
              Platform
            </label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border"
              style={{
                background: "var(--gio-surface-alt)",
                borderColor: "var(--gio-border)",
                color: "var(--gio-text)",
              }}>
              <option value="">— None —</option>
              <option value="onlyfans_vip">OnlyFans VIP</option>
              <option value="onlyfans_free">OnlyFans Free</option>
              <option value="justforfans">JustForFans</option>
              <option value="rawfuckclub">RawFuckClub</option>
              <option value="faphouse">Faphouse</option>
              <option value="x_twitter">X / Twitter</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="bluesky">Bluesky</option>
              <option value="store">Video Store</option>
            </select>
          </div>

          {/* Content Type */}
          <div>
            <label className="text-xs font-bold uppercase mb-2 block" style={{ color: "var(--gio-muted)" }}>
              Event Type
            </label>
            <select
              value={formData.content_type}
              onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border"
              style={{
                background: "var(--gio-surface-alt)",
                borderColor: "var(--gio-border)",
                color: "var(--gio-text)",
              }}>
              <option value="shoot">Shoot</option>
              <option value="upload">Upload</option>
              <option value="promo">Promo</option>
              <option value="meeting">Meeting</option>
              <option value="travel">Travel</option>
              <option value="event">Event</option>
              <option value="deadline">Deadline</option>
              <option value="reminder">Reminder</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-bold uppercase mb-2 block" style={{ color: "var(--gio-muted)" }}>
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border"
              style={{
                background: "var(--gio-surface-alt)",
                borderColor: "var(--gio-border)",
                color: "var(--gio-text)",
              }}
              placeholder="Additional details, location, cast, etc."
            />
          </div>
        </div>

        <div className="p-5 border-t flex justify-between" style={{ borderColor: "var(--gio-border)" }}>
          <div>
            {!isNewEvent && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-ghost text-red-500 hover:bg-red-500/10 inline-flex items-center gap-2">
                <Trash2 size={16} />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2">
              <CheckCircle2 size={16} />
              {saving ? "Saving..." : isNewEvent ? "Create Event" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-11 min-w-[4rem] rounded px-3 text-xs font-bold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)] ${active ? "" : "opacity-60"}`}
      style={{
        background: active ? "var(--gio-primary)" : "transparent",
        color: active ? "#000" : "var(--gio-text)",
      }}>
      {children}
    </button>
  );
}

function RFCPeriodBreakdown({ period }) {
  const rows = period.passesRentals || [];
  const money = (value) => value == null ? "—" : `$${Number(value).toFixed(2)}`;
  const count = (value) => value == null ? "—" : Number(value).toLocaleString();
  return (
    <section className="rounded-lg border border-red-900/50 bg-red-950/20 p-4" data-testid="rfc-period-breakdown">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-red-900/40 pb-3">
        <h3 className="text-sm font-semibold text-red-200">RFC revenue breakdown</h3>
        <span className="text-xs text-zinc-400">{period.periodStart}{period.periodEnd ? ` – ${period.periodEnd}` : " · Current"}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
        <DetailMetric label="Period revenue" value={money(period.periodRevenue)} />
        <DetailMetric label="Passes & rentals" value={`${count(period.passesRentalsQuantity)} · ${money(period.passesRentalsRevenue)}`} />
        <DetailMetric label="Referral earnings" value={money(period.referralEarnings)} />
        <DetailMetric label="New joins" value={count(period.referralNewJoins)} />
        <DetailMetric label="Renewals" value={count(period.referralRenewals)} />
        <DetailMetric label="Paid memberships" value={count(period.referralPaidMemberships)} />
        <DetailMetric label="Clicks" value={count(period.referralClicks)} />
        <DetailMetric label="Revenue share" value={period.referralRevenueShare == null ? "—" : `${(Number(period.referralRevenueShare) * 100).toFixed(0)}%`} />
      </div>
      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-xs">
            <thead className="text-zinc-500"><tr><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Title</th><th className="py-2 pr-3">Site</th><th className="py-2 pr-3 text-right">Qty</th><th className="py-2 text-right">Revenue</th></tr></thead>
            <tbody className="divide-y divide-white/5">{rows.map((row, index) => <tr key={`${row.title}-${row.site}-${index}`} className="text-zinc-200"><td className="py-2 pr-3 capitalize">{row.itemType}</td><td className="py-2 pr-3">{row.displayTitle || row.title}</td><td className="py-2 pr-3">{row.site}</td><td className="py-2 pr-3 text-right">{count(row.quantity)}</td><td className="py-2 text-right">{money(row.revenue)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DetailMetric({ label, value }) {
  return <div><div className="text-[11px] text-zinc-500">{label}</div><div className="mt-0.5 font-medium text-zinc-100">{value}</div></div>;
}
